/**
 * useVisibilityAwareInterval — runs `callback` on a setInterval but:
 *   1. Skips ticks while the tab is hidden (Page Visibility API).
 *   2. Fires `callback` immediately when the tab becomes visible again,
 *      so the user always sees fresh data when they return.
 *
 * Drop-in replacement for `setInterval(callback, intervalMs)` in `useEffect`.
 *
 * Egress impact: a tab left open in the background previously hammered the
 * dashboard / monitor / errors endpoints all day. With this hook in place,
 * those polls go silent until the user actually looks at the page.
 *
 * @param {() => void | Promise<void>} callback   – the poll function
 * @param {number}                    intervalMs  – tick period in ms
 * @param {boolean}                   enabled     – set false to disable entirely
 */
import { useEffect, useRef } from "react";

export function useVisibilityAwareInterval(callback, intervalMs, enabled = true) {
  const savedCallback = useRef(callback);
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled || !intervalMs || intervalMs <= 0) return undefined;

    let intervalId = null;

    const tick = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return; // skip while user isn't looking
      }
      try {
        savedCallback.current?.();
      } catch (e) {
        // never let a polling error tear down the interval
        // eslint-disable-next-line no-console
        console.error("useVisibilityAwareInterval callback threw:", e);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // user came back — refresh immediately
        tick();
      }
    };

    intervalId = setInterval(tick, intervalMs);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      if (intervalId) clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [intervalMs, enabled]);
}

export default useVisibilityAwareInterval;
